import {
  hrExpandRecordDays,
  hrFormatDaysCount,
  hrFormatMinutes,
  type DateRangeRecord,
} from '@/modules/hr/components/hrCalendarUtils';
import { i18n } from '@/i18n';

/** Default working day length when schedule-specific minutes are unavailable. */
export const HR_DEFAULT_MINUTES_PER_DAY = 480;

export type HrTimeDisplayMode = 'HOURS' | 'DAYS' | 'BOTH';

export const HR_TIME_DISPLAY_STORAGE_KEY = 'hr-workforce-time-display-mode';

export const HR_TIME_DISPLAY_OPTIONS: { value: HrTimeDisplayMode; label: string }[] = [
  { value: 'HOURS', label: i18n.t('calendar.timeDisplay.hours', { ns: 'hr' }) },
  { value: 'DAYS', label: i18n.t('calendar.timeDisplay.days', { ns: 'hr' }) },
  { value: 'BOTH', label: i18n.t('calendar.timeDisplay.both', { ns: 'hr' }) },
];

export function hrLoadTimeDisplayMode(companyDefault?: string | null): HrTimeDisplayMode {
  try {
    const raw = localStorage.getItem(HR_TIME_DISPLAY_STORAGE_KEY);
    if (raw === 'HOURS' || raw === 'DAYS' || raw === 'BOTH') return raw;
  } catch {
    /* ignore */
  }
  const mode = companyDefault?.trim().toUpperCase();
  if (mode === 'HOURS' || mode === 'DAYS' || mode === 'BOTH') return mode;
  return 'HOURS';
}

export function hrSaveTimeDisplayMode(mode: HrTimeDisplayMode): void {
  try {
    localStorage.setItem(HR_TIME_DISPLAY_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

export function hrMinutesToDays(
  minutes: number,
  minutesPerDay = HR_DEFAULT_MINUTES_PER_DAY,
): number {
  if (!Number.isFinite(minutes) || minutes <= 0 || minutesPerDay <= 0) return 0;
  return Math.round((minutes / minutesPerDay) * 100) / 100;
}

export function hrDaysToMinutes(
  days: number,
  minutesPerDay = HR_DEFAULT_MINUTES_PER_DAY,
): number {
  if (!Number.isFinite(days) || days <= 0) return 0;
  return Math.round(days * minutesPerDay);
}

export type HrDayMinutesResolver = number | ((dayKey: string) => number);

function resolveDayMinutes(resolver: HrDayMinutesResolver, dayKey: string): number {
  const value = typeof resolver === 'function' ? resolver(dayKey) : resolver;
  if (Number.isFinite(value) && value >= 0) return value;
  return HR_DEFAULT_MINUTES_PER_DAY;
}

/**
 * Leave minutes attributed to a date range.
 * Full/half-day leave uses each day's expected minutes (schedule-aware), matching backend
 * month-end — not a fixed 8h day. Hourly leave uses {@code leaveHours}.
 */
export function hrLeaveMinutesInRange(
  row: DateRangeRecord & {
    totalDays: number;
    durationMode?: string | null;
    leaveHours?: number | null;
  },
  rangeStart: string,
  rangeEnd: string,
  minutesPerDay: HrDayMinutesResolver = HR_DEFAULT_MINUTES_PER_DAY,
): number {
  const daysInRange = hrExpandRecordDays(row, rangeStart, rangeEnd);
  if (daysInRange.length <= 0) return 0;

  const mode = (row.durationMode ?? 'FULL_DAY').toUpperCase();
  if (mode === 'HOURLY') {
    return Math.round((Number(row.leaveHours) || 0) * 60);
  }

  const halfDay = mode.includes('HALF');
  return daysInRange.reduce((sum, dayKey) => {
    const dayExpected = resolveDayMinutes(minutesPerDay, dayKey);
    return sum + (halfDay ? Math.round(dayExpected / 2) : dayExpected);
  }, 0);
}

function hrFormatDaysShort(days: number): string {
  const rounded = Math.round(days * 10) / 10;
  const display = Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1);
  return `${display}d`;
}

function hrFormatHoursShort(minutes: number): string {
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  const display = Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(1);
  return `${display}h`;
}

export function hrFormatWorkforceMetric(
  minutes: number,
  days: number,
  mode: HrTimeDisplayMode,
  minutesPerDay = HR_DEFAULT_MINUTES_PER_DAY,
): string {
  const resolvedMinutes = minutes > 0 ? minutes : hrDaysToMinutes(days, minutesPerDay);
  const resolvedDays = days > 0 ? days : hrMinutesToDays(minutes, minutesPerDay);
  if (resolvedMinutes <= 0 && resolvedDays <= 0) return '—';

  if (mode === 'HOURS') return hrFormatMinutes(resolvedMinutes);
  if (mode === 'DAYS') return hrFormatDaysCount(resolvedDays);
  return `${hrFormatDaysShort(resolvedDays)} · ${hrFormatHoursShort(resolvedMinutes)}`;
}

export function hrTotalMetricHeader(label: string, mode: HrTimeDisplayMode): string {
  if (mode === 'HOURS') {
    return i18n.isInitialized
      ? i18n.t('calendar.metricHeaderHours', { ns: 'hr', label })
      : `${label} (hrs)`;
  }
  if (mode === 'DAYS') {
    return i18n.isInitialized
      ? i18n.t('calendar.metricHeaderDays', { ns: 'hr', label })
      : `${label} (days)`;
  }
  return label;
}
